import { useQuery } from '@tanstack/react-query';
import {
  Banner,
  Box,
  InlineStack,
  Page,
  PageBody,
  PageHeader,
  PageHeaderDescription,
  PageHeaderTitle,
  Image,
  Link,
  Panel,
  PanelSection,
  Spinner,
} from '@customerio/pluma-components/react';
import { DocumentTitle } from '@/components/page/document-title';
import { SafeNavigate } from '@/services/router/safe-navigate';
import segmentLogo from '@/assets/images/integrations/segment.svg';
import { getSegmentOutIntegrationQueryOptions } from '@/services/db/segment-out-integration/queries';
import { isLegacySegmentOut } from '@/services/db/segment-out-integration/entity';
import { SegmentOutConnector } from './components/segment-out-connector';
import { useSegmentOutBreadcrumbs } from './hooks';

const PAGE_TITLE = 'Segment source';
const SOURCE_DOC_URL = 'https://docs.customer.io/journeys/segment-source/';
const DESTINATION_DOC_URL = 'https://docs.customer.io/journeys/segment-destination/';

type SegmentOutPageProps = {
  workspaceId: string;
  /** The `journeys` URL the embedded app serves, for handing a legacy record back. */
  legacyHref: string;
};

/**
 * The `/integrations/segment_out` page: connect the workspace to Segment with a
 * write key, then choose which message events to forward and how.
 *
 * A record without `use_javascript` predates this form and is configured through
 * the legacy `segdotcom` endpoint, which is not migrated (FPS-3689). Those
 * workspaces are handed back to the embedded app rather than shown a form that
 * would write to the wrong place.
 */
export default function SegmentOutPage({ workspaceId, legacyHref }: SegmentOutPageProps) {
  useSegmentOutBreadcrumbs(workspaceId);

  // Keyed off the workspace the shell resolved, not off the URL. `LastWorkspaceResolver`
  // renders this route while the pathname still says `/workspaces/last`, and reading the
  // id from the URL there throws "No workspace id in URL." straight into the error
  // boundary. The prop comes from `ConnectorRouteView`, which already waited for the
  // resolved workspace for exactly this reason.
  const catalog = useQuery(getSegmentOutIntegrationQueryOptions(Number(workspaceId)));

  const integration = catalog.data ?? null;

  // A legacy record is configured through a different endpoint, so this form would
  // write to the wrong place. Hand the workspace back to the embedded app, which
  // still serves that form, until FPS-3689 ports it.
  if (integration != null && isLegacySegmentOut(integration)) {
    return <SafeNavigate to={legacyHref} options={{ shouldReplaceHistoryEntry: true }} />;
  }

  return (
    <>
      <DocumentTitle title={PAGE_TITLE} />
      <Page>
        <PageHeader>
          <PageHeaderTitle>
            <InlineStack alignBlock="center" gap="100">
              <Image src={segmentLogo} alt="" size="icon-v2-xl" objectFit="contain" />
              {PAGE_TITLE}
            </InlineStack>
          </PageHeaderTitle>
          <PageHeaderDescription>
            Segment helps you manage data from multiple sources in one place. With Customer.io as a Segment Source, we
            send message events to Segment. Learn more about{' '}
            <Link href={SOURCE_DOC_URL} variant="secondary" isExternal>
              Customer.io as a Segment Source
            </Link>
            . You can also use{' '}
            <Link href={DESTINATION_DOC_URL} variant="secondary" isExternal>
              Customer.io as a Segment Destination
            </Link>
            .
          </PageHeaderDescription>
        </PageHeader>

        <PageBody>
          {catalog.isError ? (
            <Banner
              variant="critical"
              title="We couldn't load this integration"
              description="Refresh the page to try again."
            />
          ) : catalog.isPending ? (
            <Panel withSectionBorders>
              <PanelSection>
                <Box textAlign="center" p="300">
                  <Spinner />
                </Box>
              </PanelSection>
            </Panel>
          ) : (
            // Remount on connect or disconnect: the settings draft is seeded from
            // the record, so a new record must start from a new draft.
            <SegmentOutConnector
              key={integration?.id ?? 'not-connected'}
              integration={integration}
              workspaceId={workspaceId}
            />
          )}
        </PageBody>
      </Page>
    </>
  );
}
